/**
 * The one line the other players read when somebody stops being there.
 *
 * Three ways to leave and three sentences, because they mean different things to the people left
 * behind. Someone who quit is not coming back, someone who dropped might be back in a moment, and
 * someone whose seat expired is gone after everybody already waited. A single "player left" would
 * throw all three away, and the middle one is the one that stops the others from giving up on a
 * game that is about to be four again.
 *
 * Pure, and separate from `sessionErrors` on purpose: those are failures this machine had, these
 * are things that happened to somebody else, and neither is a good place to keep the other.
 *
 * The one line this machine reads about itself lives here too, for the same reason the others do:
 * it is said on two screens, the lobby and a running game, and one sentence in two places drifts.
 */

import type { LeftReason } from '@/net/wire/lobbyFrame';

export interface SessionNotice {
  readonly text: string;
}

export function leftNotice(name: string, reason: LeftReason): SessionNotice {
  switch (reason) {
    case 'quit':
      return { text: `${name} left.` };
    case 'lost':
      return { text: `${name} lost connection. Holding their place.` };
    case 'timedOut':
      return { text: `${name} did not come back.` };
  }
}

export function joinedNotice(name: string): SessionNotice {
  return { text: `${name} joined.` };
}

export function rejoinedNotice(name: string): SessionNotice {
  return { text: `${name} is back.` };
}

/**
 * What this machine reads while it is trying to get back in, wherever it happens to be looking.
 *
 * A countdown rather than a spinner, because the wait is bounded and the number is the whole point:
 * it says both that something is happening and that it will not go on forever. Rounded up, so the
 * last second shows a one rather than a zero somebody is still waiting on.
 */
export function rejoiningLine(msLeft: number): string {
  return `Lost the host. Trying to get back in, ${String(Math.ceil(msLeft / 1000))}s left.`;
}
